// Ask about this (JEV.md §4) on the web: the passages that answer the question come from the decisions model (OpenAI
// Decisions or Jev, OPENAI.md §7) when the server offers one, else the paragraphs around where you are; the answer
// comes from the Ask model and, if "Speak answers" is on, is read aloud with the article's voice.
import { account } from "../api/account";
import { api } from "../api/client";
import { TextClient, unavailable } from "../levels/textClient";
import { sourceParagraphs, title, type Article } from "../model/article";
import { askModel, decisionsBackend } from "../models";
import { narrate } from "../narration/tts";
import { wav } from "../narration/engine";
import { player } from "../narration/player";
import { settings } from "../settings";
import * as db from "../store/db";
import { askPrompt, askRequests, askSelect, fromDecisions, toDecisions } from "./jev";

export interface AskRecord { id: string; date: number; question: string; answer: string; passages: { index: number; source: number; preview: string }[]; by: string | null; error?: string }

function backendInUse(): "openai" | "jev" | null {
  const b = decisionsBackend();
  const oa = account.available("openai"), jev = account.available("typesafe");
  if (b === "openai") return oa ? "openai" : null;
  if (b === "jev") return jev ? "jev" : null;
  return oa ? "openai" : jev ? "jev" : null;
}

async function scores(bodies: any[], backend: "openai" | "jev", signal: AbortSignal): Promise<Record<string, any> | null> {
  try {
    const replies = await Promise.all(bodies.map(async (b) => {
      const res = backend === "openai"
        ? await api(`/v1/proxy/openai/v1/decisions`, { method: "POST", json: toDecisions(b), signal })
        : await api(`/v1/proxy/typesafe/v1/systemone`, { method: "POST", json: b, signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      return backend === "openai" ? fromDecisions(j).answers : (j.answers ?? {});
    }));
    return Object.assign({}, ...replies);
  } catch { return null; }
}

export async function history(id: string): Promise<AskRecord[]> {
  return (await db.get<AskRecord[]>("ask", id).catch(() => undefined)) ?? [];
}

/** Answers a question about an article; `position` = the source paragraph being read or narrated. */
export async function ask(a: Article, question: string, position: number, signal: AbortSignal,
  onPhase: (p: "retrieving" | "answering" | "speaking") => void): Promise<AskRecord> {
  const model = askModel();
  const why = unavailable(model);
  if (why) throw new Error("Ask about this: " + why);
  const source = sourceParagraphs(a);
  const skipped = new Set(a.meta.skipped ?? []);
  const kept = source.map((_, i) => i).filter((i) => !skipped.has(i));
  const paragraphs = kept.map((i) => source[i]);
  if (!paragraphs.length) throw new Error("There's no text to ask about yet.");
  let pos = kept.findIndex((i) => i >= position);
  if (pos < 0) pos = paragraphs.length - 1;
  onPhase("retrieving");
  const backend = backendInUse();
  let answers: Record<string, any> | null = null;
  const bodies = askRequests(question, paragraphs);
  if (backend && bodies.length) answers = await scores(bodies, backend, AbortSignal.any([signal, AbortSignal.timeout(10_000)]));
  const selected = askSelect(paragraphs, answers, pos);
  onPhase("answering");
  const body = askPrompt(question, title(a), a.meta.author ?? null, false, paragraphs, selected, pos);
  const answer = (await new TextClient(model).chat(body, { signal, timeoutMs: 30_000 })).trim();
  const record: AskRecord = { id: crypto.randomUUID(), date: Date.now(), question, answer, by: answers ? (backend === "openai" ? "OpenAI Decisions" : "Jev") : null,
    passages: selected.map((i) => ({ index: i, source: kept[i], preview: paragraphs[i].length > 100 ? paragraphs[i].slice(0, 100) + "…" : paragraphs[i] })) };
  const all = await history(a.id);
  await db.put("ask", a.id, [...all, record].slice(-20)).catch(() => {});
  return record;
}

let answerAudio: HTMLAudioElement | null = null;

/** Reads the answer aloud with the article's voice; the browser's own voice if that fails. */
export async function speak(a: Article, text: string, signal: AbortSignal): Promise<void> {
  stopSpeaking();
  try {
    const v = player.voiceFor(a);
    const pcm = await narrate(text, v, { style: settings.narrationStyle, signal });
    const el = (answerAudio = new Audio(URL.createObjectURL(wav(pcm, 24000))));
    el.playbackRate = settings.playbackRate;
    await new Promise<void>((resolve) => {
      el.onended = () => resolve();
      signal.addEventListener("abort", () => { el.pause(); resolve(); }, { once: true });
      el.play().catch(() => resolve());
    });
  } catch {
    if (signal.aborted || !("speechSynthesis" in window)) return;
    await new Promise<void>((resolve) => {
      const u = new SpeechSynthesisUtterance(text);
      u.onend = () => resolve(); u.onerror = () => resolve();
      signal.addEventListener("abort", () => { speechSynthesis.cancel(); resolve(); }, { once: true });
      speechSynthesis.speak(u);
    });
  }
}

export function stopSpeaking() {
  answerAudio?.pause();
  answerAudio = null;
  if ("speechSynthesis" in window) speechSynthesis.cancel();
}
